import * as THREE from 'three';
import {
  PX_PER_METRE,
  buttonTextOptions,
  fitText,
  LABEL_TEXT,
  labelLines,
  type MeasureText,
} from '../engine/text.js';

/**
 * Canvas-texture UI: panels, buttons and floating labels.
 *
 * Ported from prototypes/ar-measure, not from the solar-system prototype.
 * docs/04-lesson-format.md requires >=1024 px per metre and auto-shrinking
 * title and body; only the AR prototype did both. The VR one used 512 px/m and
 * plain wrapping, which overflows with long Dutch words.
 */

export type PanelStyle = {
  background: string;
  accent: string;
  title: string;
  body: string;
};

export const panelStyles = {
  lectern: { background: '#0f1830f2', accent: '#5aa9ff', title: '#ffffff', body: '#dfe7ff' },
  hotspot: { background: '#14261cf2', accent: '#63d68a', title: '#ffffff', body: '#d7f2e0' },
  correct: { background: '#12291bf2', accent: '#3ec46d', title: '#ffffff', body: '#d7f2e0' },
  wrong: { background: '#2a1d12f2', accent: '#e0a526', title: '#ffffff', body: '#f6e6cf' },
  ar: { background: '#ffffffef', accent: '#1e6fd9', title: '#1b2433', body: '#44506a' },
} as const satisfies Record<string, PanelStyle>;

export type PanelContent = {
  title?: string;
  text?: string;
  style?: PanelStyle;
  /**
   * A corner kept clear at the top right, metres from the panel's right and
   * top edges: "Voorlezen" sits there (lesson-schema's `voiceReserve`). The
   * title wraps short of it and the text starts below it.
   */
  reserve?: { width: number; height: number };
  /**
   * Metres at the panel's bottom kept clear of the text: the furnish step's room buttons and
   * palette stand there (ADR-037, render/lectern.ts's `furnishLectern`).
   */
  bottom?: number;
};

function canvasMeasure(ctx: CanvasRenderingContext2D): MeasureText {
  return (text, fontSize, bold) => {
    ctx.font = `${bold ? 'bold ' : ''}${fontSize}px system-ui, sans-serif`;
    return ctx.measureText(text).width;
  };
}

/**
 * The least a lectern panel that sizes to its words is tall, metres (11 Oct 2026): room for a title
 * and three lines, and for "Voorlezen" in its corner with a line beside it.
 */
export const LECTERN_PANEL_MIN = 0.3;

/** Where a panel's words go: each line's text, size and baseline, in canvas pixels from its top. */
type PanelLine = { text: string; font: string; y: number };

/**
 * A flat panel whose texture can be redrawn in place.
 *
 * With `fit` (the VR lectern, 11 Oct 2026: Pieter, "when there is not much text, don't make the
 * text box bigger than needed") it is only as tall as its words, between `fit.min` and the size it
 * was made at, its bottom edge at `fit.bottom` -- so the buttons under it stay where they are, and it
 * shrinks from the top, leaving more of the view. The words are laid out exactly as at its full
 * size (the same wrapping and font sizes), and its canvas keeps 1024 px a metre at every height.
 */
export class Panel {
  readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  readonly #canvas: HTMLCanvasElement;
  readonly #ctx: CanvasRenderingContext2D;
  readonly #texture: THREE.CanvasTexture;
  readonly #fit: { min: number; bottom: number } | undefined;
  #style: PanelStyle;
  #height: number;

  constructor(
    readonly width: number,
    /** Its full size: what it is drawn at without `fit`, and the most it grows to with it. */
    readonly maxHeight: number,
    style: PanelStyle = panelStyles.lectern,
    options: {
      depthTest?: boolean;
      renderOrder?: number;
      /** Sized to its words, its bottom edge at `bottom` (metres in its parent), never under `min`. */
      fit?: { min: number; bottom: number };
    } = {},
  ) {
    this.#style = style;
    this.#fit = options.fit;
    this.#height = maxHeight;
    this.#canvas = document.createElement('canvas');
    this.#canvas.width = Math.round(width * PX_PER_METRE);
    this.#canvas.height = Math.round(maxHeight * PX_PER_METRE);
    this.#ctx = this.#canvas.getContext('2d')!;

    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    this.#texture.anisotropy = 4;

    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(width, maxHeight),
      new THREE.MeshBasicMaterial({
        map: this.#texture,
        transparent: true,
        depthTest: options.depthTest ?? true,
      }),
    );
    if (options.renderOrder !== undefined) this.mesh.renderOrder = options.renderOrder;
    if (this.#fit) this.mesh.position.y = this.#fit.bottom + maxHeight / 2;
  }

  /** How tall it is now, metres: its full size, or with `fit` as tall as its words. */
  get height(): number {
    return this.#height;
  }

  set visible(value: boolean) {
    this.mesh.visible = value;
  }
  get visible(): boolean {
    return this.mesh.visible;
  }

  draw({ title = '', text = '', style, reserve, bottom = 0 }: PanelContent): void {
    if (style) this.#style = style;
    const s = this.#style;
    const ctx = this.#ctx;
    const w = this.#canvas.width;
    // Laid out at the full size, as it always was: a smaller panel only cuts off the empty part.
    const full = Math.round(this.maxHeight * PX_PER_METRE);
    const pad = Math.round(0.042 * PX_PER_METRE);
    const measure = canvasMeasure(ctx);
    const lines: PanelLine[] = [];

    const maxWidth = w - pad * 2 - 16;
    // Short of the corner: from the text's left edge to the corner's.
    const titleWidth = reserve
      ? Math.min(maxWidth, w - Math.round(reserve.width * PX_PER_METRE) - pad - 16)
      : maxWidth;
    let y = pad;

    if (title) {
      const fitted = fitText(
        title,
        { maxWidth: titleWidth, maxHeight: full * 0.3, fontSize: 56, minFontSize: 26, bold: true },
        measure,
      );
      for (const line of fitted.lines) {
        y += fitted.fontSize;
        lines.push({ text: line, font: `bold ${fitted.fontSize}px system-ui, sans-serif`, y });
        y += fitted.fontSize * 0.28;
      }
      y += fitted.fontSize * 0.25;
    }
    // The text runs the panel's full width, so it starts below the corner.
    if (reserve && text) y = Math.max(y, Math.round(reserve.height * PX_PER_METRE));
    const titleLines = lines.length;

    if (text) {
      const fitted = fitText(
        text,
        {
          maxWidth,
          maxHeight: full - y - pad - Math.round(bottom * PX_PER_METRE),
          fontSize: 42,
          minFontSize: 20,
        },
        measure,
      );
      for (const line of fitted.lines) {
        y += fitted.fontSize;
        lines.push({ text: line, font: `${fitted.fontSize}px system-ui, sans-serif`, y });
        y += fitted.fontSize * 0.3;
      }
    }

    // As tall as the words and a margin; the corner "Voorlezen" takes counts as words. A panel
    // whose lower part holds buttons (a furnish step's rooms) keeps its full size.
    const fit = this.#fit;
    const words = Math.max(y, reserve ? Math.round(reserve.height * PX_PER_METRE) : 0) + pad;
    const h =
      fit && bottom === 0
        ? Math.min(full, Math.max(Math.ceil(fit.min * PX_PER_METRE), Math.ceil(words)))
        : full;
    this.#resize(h, full);

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = s.background;
    ctx.beginPath();
    ctx.roundRect(0, 0, w, h, 40);
    ctx.fill();

    // Accent stripe down the leading edge; the prototypes used it to signal
    // which kind of panel this is (tour, hotspot, right, wrong).
    ctx.fillStyle = s.accent;
    ctx.beginPath();
    ctx.roundRect(0, 0, 16, h, [40, 0, 0, 40]);
    ctx.fill();

    lines.forEach((line, i) => {
      ctx.fillStyle = i < titleLines ? s.title : s.body;
      ctx.font = line.font;
      ctx.fillText(line.text, pad + 16, line.y);
    });

    this.#texture.needsUpdate = true;
  }

  /** A canvas `h` pixels tall and a plane to match, its bottom edge kept where `fit` says. */
  #resize(h: number, full: number): void {
    const fit = this.#fit;
    if (!fit || h === this.#canvas.height) return;
    this.#canvas.height = h;
    this.#height = h === full ? this.maxHeight : h / PX_PER_METRE;
    // A texture of another size is a new one on the GPU: let it go, and the next frame uploads it.
    this.#texture.dispose();
    this.mesh.geometry.dispose();
    this.mesh.geometry = new THREE.PlaneGeometry(this.width, this.#height);
    this.mesh.position.y = fit.bottom + this.#height / 2;
  }

  dispose(): void {
    this.#texture.dispose();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}

export type ButtonKind =
  'primary' | 'secondary' | 'correct' | 'wrong' | 'muted' | 'voice' | 'voiceOn';

/**
 * A drawn icon before a button's label: a speaker ("Voorlezen") or a square
 * ("Stop"). Drawn, not typed: 🔊 is an emoji the Quest browser's fonts may
 * not have, and a missing glyph is an empty box on a child's lectern.
 */
export type ButtonIcon = 'speaker' | 'stop';

function drawIcon(
  ctx: CanvasRenderingContext2D,
  icon: ButtonIcon,
  x: number,
  middle: number,
  size: number,
  colour: string,
): void {
  ctx.save();
  ctx.fillStyle = colour;
  ctx.strokeStyle = colour;
  if (icon === 'stop') {
    const side = size * 0.72;
    ctx.beginPath();
    ctx.roundRect(x + (size - side) / 2, middle - side / 2, side, side, side * 0.12);
    ctx.fill();
  } else {
    // The box and the cone, then two waves.
    const s = size;
    ctx.beginPath();
    ctx.moveTo(x, middle - s * 0.16);
    ctx.lineTo(x + s * 0.2, middle - s * 0.16);
    ctx.lineTo(x + s * 0.46, middle - s * 0.4);
    ctx.lineTo(x + s * 0.46, middle + s * 0.4);
    ctx.lineTo(x + s * 0.2, middle + s * 0.16);
    ctx.lineTo(x, middle + s * 0.16);
    ctx.closePath();
    ctx.fill();
    ctx.lineWidth = Math.max(2, s * 0.08);
    ctx.lineCap = 'round';
    for (const radius of [0.2, 0.38]) {
      ctx.beginPath();
      ctx.arc(x + s * 0.5, middle, s * radius, -Math.PI / 4, Math.PI / 4);
      ctx.stroke();
    }
  }
  ctx.restore();
}

const buttonColours: Record<ButtonKind, { base: string; hover: string; label: string }> = {
  primary: { base: '#2b6fe0', hover: '#4d8ef5', label: '#ffffff' },
  secondary: { base: '#3a4a6b', hover: '#4d6186', label: '#ffffff' },
  correct: { base: '#2e8b57', hover: '#2e8b57', label: '#ffffff' },
  wrong: { base: '#a33a3a', hover: '#a33a3a', label: '#ffffff' },
  muted: { base: '#2c3d63', hover: '#3d5382', label: '#cfd9ef' },
  // "Voorlezen": on the panel itself, so lighter than the panel's navy to be
  // found, and not Verder's blue, which is the way on.
  voice: { base: '#3d6fb0', hover: '#5288cf', label: '#ffffff' },
  // "Voorlezen: aan": lit in the panel's own accent, dark letters -- on, at a glance.
  voiceOn: { base: '#8cc4ff', hover: '#b0d6ff', label: '#0b1a33' },
};

/**
 * A button big enough to hit with a controller ray or a pinch.
 * docs/10-pedagogy.md asks for big targets; the default is 42 cm x 14 cm.
 */
export class Button {
  readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  readonly #canvas: HTMLCanvasElement;
  readonly #ctx: CanvasRenderingContext2D;
  readonly #texture: THREE.CanvasTexture;
  #hovered = false;
  #icon: ButtonIcon | undefined;
  #kind: ButtonKind | undefined;

  constructor(
    public label: string,
    readonly onSelect: () => void,
    readonly options: {
      width?: number;
      height?: number;
      kind?: ButtonKind;
      icon?: ButtonIcon;
    } = {},
  ) {
    this.#icon = options.icon;
    this.#kind = options.kind;
    const width = options.width ?? 0.42;
    const height = options.height ?? 0.14;

    this.#canvas = document.createElement('canvas');
    this.#canvas.width = Math.round(width * PX_PER_METRE);
    this.#canvas.height = Math.round(height * PX_PER_METRE);
    this.#ctx = this.#canvas.getContext('2d')!;

    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;

    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      // The pill's corners are clear canvas. Blended but still written to the
      // depth buffer, a clear corner drawn before the panel behind it hid the
      // panel there, and the world showed through in grey (Pieter's Quest
      // test, 29 Sep 2026): a clear pixel is dropped instead.
      new THREE.MeshBasicMaterial({ map: this.#texture, transparent: true, alphaTest: 0.02 }),
    );
    this.mesh.userData['button'] = this;
    this.#draw();
  }

  get kind(): ButtonKind {
    return this.#kind ?? 'primary';
  }

  set hovered(value: boolean) {
    if (this.#hovered === value) return;
    this.#hovered = value;
    this.#draw();
  }

  /** A new face: what it says, its icon and its colours ("Voorlezen" / "Voorlezen: aan"). */
  setFace(face: { label: string; icon?: ButtonIcon; kind?: ButtonKind }): void {
    this.label = face.label;
    this.#icon = face.icon;
    this.#kind = face.kind;
    this.#draw();
  }

  #draw(): void {
    const colours = buttonColours[this.kind];
    const ctx = this.#ctx;
    const { width: w, height: h } = this.#canvas;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = this.#hovered ? colours.hover : colours.base;
    ctx.beginPath();
    ctx.roundRect(0, 0, w, h, h / 2);
    ctx.fill();

    // An icon takes a square of half the button's height, and a gap, off the label's width.
    const icon = this.#icon;
    const iconSize = icon ? h * 0.5 : 0;
    const gap = icon ? h * 0.18 : 0;
    const options = buttonTextOptions(w, h);
    const measure = canvasMeasure(ctx);
    const fitted = fitText(
      this.label,
      { ...options, maxWidth: options.maxWidth - iconSize - gap },
      measure,
    );
    const widest = Math.max(...fitted.lines.map((line) => measure(line, fitted.fontSize, true)));
    // The icon and the label centred together.
    const centre = w / 2 + (iconSize + gap) / 2;

    ctx.fillStyle = colours.label;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const lineHeight = fitted.fontSize * 1.18;
    const top = h / 2 - ((fitted.lines.length - 1) * lineHeight) / 2;
    fitted.lines.forEach((line, i) => {
      ctx.font = `bold ${fitted.fontSize}px system-ui, sans-serif`;
      ctx.fillText(line, centre, top + i * lineHeight);
    });
    if (icon)
      drawIcon(ctx, icon, centre - widest / 2 - gap - iconSize, h / 2, iconSize, colours.label);
    ctx.textAlign = 'start';
    ctx.textBaseline = 'alphabetic';

    this.#texture.needsUpdate = true;
  }

  dispose(): void {
    this.#texture.dispose();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}

/**
 * A name floating above an object, always facing the viewer: one line. `lines` 2 (a direction badge's title
 * in a 360° photo, docs/04): up to two lines at the same letter height on a label 1.5 times as wide
 * (`LABEL_TEXT.wide`), as tall as the lines it has.
 */
export function makeLabel(text: string, scale = 1, lines = 1): THREE.Sprite {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const measure: MeasureText = (t, size, bold) => {
    ctx.font = `${bold ? 'bold ' : ''}${size}px system-ui, sans-serif`;
    return ctx.measureText(t).width;
  };
  const fitted =
    lines <= 1
      ? fitText(
          text,
          { maxWidth: 480, maxHeight: 100, fontSize: 64, minFontSize: 28, bold: true },
          measure,
        )
      : labelLines(text, lines, measure, LABEL_TEXT.maxWidth * LABEL_TEXT.wide);
  const drawn = lines <= 1 ? [fitted.lines[0] ?? text] : fitted.lines;
  const wide = lines <= 1 ? 1 : LABEL_TEXT.wide;
  // Sized after measuring: setting a canvas's size resets its context.
  canvas.width = LABEL_TEXT.width * wide;
  canvas.height = LABEL_TEXT.lineHeight * drawn.length;
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = '#000000';
  ctx.shadowBlur = 12;
  ctx.font = `bold ${fitted.fontSize}px system-ui, sans-serif`;
  drawn.forEach((line, i) =>
    ctx.fillText(line, canvas.width / 2, LABEL_TEXT.lineHeight * (i + 0.5)),
  );

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false }),
  );
  sprite.scale.set(1.2 * scale * wide, 0.3 * scale * drawn.length, 1);
  return sprite;
}
